(ns practitest.frontend.events
  (:require
    [ajax.core :as ajax]
    [clojure.set :refer [rename-keys]]
    [clojure.string :as str]
    [clojure.walk :refer [postwalk]]
    [day8.re-frame.http-fx]
    [goog.object :as gobj]
    [goog.string :as gstr]
    [practitest.frontend.common-entities.metadata :refer [get-entity-name entities-metadata]]
    [practitest.frontend.common.dom :as dom]
    [practitest.frontend.common.format-utils :as fu]
    [practitest.frontend.common.i18n :refer [get-translation] :as i18n]
    [practitest.frontend.common.logger :as log]
    [practitest.frontend.common.types :as types]
    [practitest.frontend.common.websocket :as ws]
    [practitest.frontend.components.grid.localstorage :as grid-storage]
    [practitest.frontend.components.notifications :as notifications]
    [practitest.frontend.kanban :as kanban]
    [practitest.frontend.routing.backend-routes :as broutes]
    [practitest.frontend.routing.router :as router]
    [practitest.frontend.subs :as subs]
    [practitest.frontend.export-svg :as export-svg]
    [re-frame.core :as re-frame]))

;; NOTE: this is overwritten in compile time when compiling release version 
(goog-define FRONTEND_VERSION "development")

;; TODO: move to xbot ?
(def xbot-client-id-field
  {:custom?                false
   :system?                true
   ;; TODO: id ? - do we really need it ?
   ;:id                     99
   :label                  "Client Type"
   :name                   :client_id
   :type                   :text})

;; effects

;;; ::redirect effect
(re-frame/reg-fx
  ::redirect
  (fn [uri]
    (let [location (gobj/get js/document "location")]
      (.replace location uri))))

(re-frame/reg-fx
  ::redirect-delayed
  (fn [uri]
    (let [location (gobj/get js/document "location")]
     ;; Had to add this delay for steps editor because common entities code is trying to redirect
     ;; when steps form is "dirty" so warning is displayed when trying to trigger redirect
     ;; TODO: callback on entity-saved ? We don't really need this, it's just a quickest fix
      (js/setTimeout #(.replace location uri) 100))))

(re-frame/reg-fx
 ::print-svg
 (fn [svg]
   (export-svg/print-svg (update svg :svg-element deref))))

(def error-unsupported-format
  "Unable to export svg to %s - unsupported format")

(re-frame/reg-fx
 ::export-svg
 (fn [{:keys [format svg-element filename] :as svg}]
   (if-some [export (export-svg/format->exporter format)]
     (-> {:svg-element @svg-element}
         export 
         (.then #(export-svg/trigger-download % filename)))
     (js/console.error (gstr/format error-unsupported-format (name format))))))

;;; ::navigate effect
;; Uses .assign to preserve browser history (back button works)
(re-frame/reg-fx
 ::navigate
 (fn [uri]
   (dom/navigate! uri)))

;; event handlers

(re-frame/reg-event-fx
 ::navigate
 (fn [_ [_ uri]]
   {::navigate uri}))

(re-frame/reg-event-fx
 ::print-svg
 (fn [_ [_ svg]]
   {::print-svg svg}))

(re-frame/reg-event-fx
 ::export-svg
 (fn [_ [_ svg]]
   {::export-svg svg}))

;;; ::api-error
(defn api-error [{db :db} [_ event
                           {:keys [message]
                            :as options}
                           response]]
  ;; TODO: rollbar integration ?
  (let [auto-close (get-in response [:response :auto_close])
        errors (get-in response [:response :errors])
        server-message (cond
                         ;; Errors can be a map of errors (in e.g. entity
                         ;; saving methods with fields validation)
                         (map? errors)
                         (fu/format-server-error-message errors)

                         ;; Just plain string (e.g. in Jira integration code)
                         (string? errors)
                         errors

                         ;; No message returned or empty sequence is returned - use default one
                         (nil? (seq errors))
                         nil

                         (coll? errors)
                         (str/join ", " errors)

                         ;; Not really sure what happens here, just print it as pr-str and dump to user
                         :else
                         (with-out-str
                           (pr-str errors)))]
    {:db (update db :progress disj event)
     :fx [(when-let [error (or server-message message)]
            ;; Show either server-provided error message or our custom message (optional)
            ;; Doesn't show notification if message is not provided
            ;; TODO: optional raw-html? - it's only required in Jira linkage errors as far as I know ?
            [:dispatch [::notifications/error2 error {:raw-html? true
                                                      :auto-close auto-close}]])]}))

(re-frame/reg-event-fx
  ::api-error
  api-error)

(re-frame/reg-event-fx
  ::handle-lms-success
  (fn [{db :db} [_ response]]
    {:db         (update db :progress disj ::add-lms-participant)
     :dispatch-n [(if (:success response)
                    [::notifications/success2 (get-translation "navigation.help-menu.get-certified.success" "Success - Please check your email for registration details")]
                    [::notifications/error2 (get-translation "navigation.help-menu.get-certified.error" "Please Contact Support")])]}))

(re-frame/reg-event-fx
  ::add-lms-participant
  (fn [{:keys [db]} [_]]
    {:db (update db :progress conj ::add-lms-participant)
     :http-xhrio
     {:method          :post
      :uri             (broutes/get-url "lms_webhooks#sign_lms")
      :params          {:email (get-in db [:current-user :email])
                        :name (get-in db [:current-user :name])}
      :format          (ajax/json-request-format)
      :response-format (ajax/json-response-format {:keywords? true})
      :on-success      [::handle-lms-success]
      :on-failure      [::api-error ::add-lms-participant {}]}}))

;; TODO: well, i don't like it but seems like we still need it
(re-frame/reg-event-db
  ::set-current-entity-type
  (fn [db [_ entity-type]]
    (assoc db :current-entity entity-type)))

(re-frame/reg-event-db
  ::set-new-entity-traceability
  (fn [db [_ entity-type]]
    (let [current-entity (:current-entity db)
          traceability (get-in entities-metadata [current-entity :init-traceability])]
      (assoc-in db [:entity entity-type :traceability] traceability))))

(re-frame/reg-event-db
  ::clear-current-entity
  (fn [db [_ entity]]
    (-> db
        (update-in [:entity entity] dissoc :current)
        (cond-> (= entity :testset)
          (update-in [:entity entity] dissoc :instances-state))
        (cond-> (= entity :test)
          (update-in [:entity entity] dissoc :steps)))))

;; Merge :referenced_users from a server response into the :missing-users
;; cache keyed by id. Producers (e.g. comments, history) ship referenced_users
;; alongside the entity payload so attribution renders for users no longer
;; in the project user list.
(defn merge-referenced-users [db response]
  (let [users (:referenced_users response)]
    (if (seq users)
      (update db :missing-users (fnil merge {})
              (into {} (map (juxt :id identity)) users))
      db)))

;; Underlying issue here is that we're converting keys to keywords
;; and backend sends us linked lists as JS maps with keys containing slashes
;; so we're doing this hacky stuff to fix it
;; (name (keyword "foo/bar/baz")) => "foo"
;; (.-fqn (keyword "foo/bar/baz")) => "foo/bar/baz"
(defn keyword->string
  [kw]
  ;; TODO: fix this ({:keywords? false} in get-fields call)
  (.-fqn ^Keyword kw))

;; Copy-pasted from clojure.walk, modified to support namespaced keys
(defn stringify-keys
  "Recursively transforms all map keys from keywords to strings."
  {:added "1.1"}
  [m]
  (let [f (fn [[k v]] (if (keyword? k) [(keyword->string k) v] [k v]))]
    ;; only apply to maps
    (postwalk (fn [x] (if (map? x) (into {} (map f x)) x)) m)))

; we need all fields here to assoc linked-lists relations
(defn convert-field [fields field]
  (let [date-time-fields #{:created_at
                           :updated_at
                           :last_run
                           :planned_execution}
        parent-lists     (->> fields
                              (filter (fn [field]
                                        (and (contains? #{"list" "linkedlist"} (:type field))
                                             (:custom field))))
                              (map (fn [field]
                                     [(:id field)
                                      (keyword (:name field))]))
                              (into {}))
        issue-status     (first (filter #(= "issue_status" (:name %)) fields))
        field-key        (keyword (:name field))
        field-type       (cond
                           (or (contains? date-time-fields field-key)
                               (= (:type field) "datetime"))
                           :date-time

                           ;; Workaround for defect age field which is a number but does not have type set at backend.
                           ;; Decided to change it here, because I'm not sure about impact if I change it on backend
                           (= field-key :defect_age)
                           :number

                           (= field-key :scenario)
                           :bdd-scenario

                           (= field-key :num_of_results)
                           :num_of_results

                           (= field-key :version_label)
                           :version_label

                           :else
                           (keyword (:type field)))]
    (cond-> {:id                    (:id field)
             :name                  field-key
             :type                  field-type
             :label                 (:label field)
             :custom?               (:custom field)
             :mandatory?            (:mandatory? field)
             :system?               (:system field)
             :allow-add-list-value? (:allow_add_list_value field)
             :bugtracker            (:bugtracker field)
             :global?               (:is_global? field)}

      (:parent_list_id field)
      (assoc :parent_list_id (:parent_list_id field)
             :parent_list_field_name (parent-lists (:parent_list_id field)))

      ;; Workaround for issue status resolution which is technically a linked list
      ;; but backend doesn't have parent list id set for it
      (= :status_resolution field-key)
      (assoc :parent_list_id (:id issue-status)
             :parent_list_field_name :issue_status)

      (:values field)
      (assoc :possible-values (stringify-keys (:values field))))))

(defn process-default-user-value
  [value]
  (when (string? value)
    (cond
      (str/starts-with? value "Group-")
      {:id (fu/try-parse-number (str/replace-first value #"Group-" ""))
       :type "group"}

      (str/starts-with? value "User-")
      {:id (fu/try-parse-number (str/replace-first value #"User-" ""))
       :type "user"})))

(defn- bugtracker-supported-for-entity? [bugtracker entity field]
  (boolean
    (and bugtracker
         (true? (get bugtracker (keyword  (clojure.string/lower-case (name entity))))))))

(defn get-test-status-default [use-test-workflow? default-val]
  (if (and use-test-workflow? (nil? default-val))
    "Draft"
    default-val))

;;; ::got-fields
(defn got-fields [db [_ fields]]
  ;; clean up the fields a little before adding it to the app-db
  (let [;; TODO: move to backend ?
        readonly-fields  #{:updated_at
                           :created_at
                           :run_status
                           :last_run
                           :author
                           :defect_age
                           :closed_at
                           :closed_by
                           :estimated_duration
                           :duration_average
                           :actual_duration
                           :requirement_status
                           :ai_enhanced}
        use-test-workflow? @(re-frame/subscribe [::subs/use-test-workflow?])]
    (assoc db
           :fields
           (->> fields
                (map (fn [backend-field]
                       (let [field (convert-field fields backend-field)]
                         [(:name field) field])))
                ;; TODO: from backend
                (into {:kanban_lane kanban/kanban-field
                       :client_id xbot-client-id-field}))
           :integrated-fields
           (into {}
                 (map (juxt :name :bugtracker)
                      (filter #(and (:name %) (:bugtracker %)) fields)))
           :entity-fields
           ;; we need to keep order of fields
           (let [counter (atom 0)]
             (reduce (fn [acc field]
                       (let [entities (:entities field)]
                         (reduce (fn [acc1 [entity options]]
                                   (let [field-id (keyword (:name field))
                                         type (keyword (:type field))]
                                     (update acc1 (types/backend->frontend entity)
                                             assoc field-id
                                             (rename-keys (assoc options
                                                                 :read-only? (contains? readonly-fields field-id)
                                                                 ;; TODO: something better ?
                                                                 :order (swap! counter inc)
                                                                 ;; Special processing for default user value
                                                                 :default (cond
                                                                            ;; Use "Draft" as the default test status
                                                                            ;; when test workflow is enabled
                                                                            (= field-id :test_status)
                                                                            (get-test-status-default use-test-workflow? (:default options))
                                                                            (= type :user)
                                                                            (process-default-user-value (:default options))
                                                                            :else (:default options))
                                                                 ;; Only add bugtracker if supported for this entity 
                                                                 :bugtracker (bugtracker-supported-for-entity? (:bugtracker field) entity field)
                                                                 :test  (:mandatory? field))
                                                          {:mandatory :mandatory?}))))
                                 acc
                                 entities)))
                     {}
                     fields)))))

(re-frame/reg-event-db
  ::got-fields
  got-fields)

;; Small optimization to prevent double field fetch on initialization
;; Caused by legacy code. Right now we have double calls to initialize-db on e.g.
;; tests page, because it has a mix of old, new and new new components
;; One initialize-db is performed by navigation UI
;; And another is done by common-entities namespace on webcomponent init
;; As it's done concurrently I decided to implement simple debouncer for fetch fields call
;; Should all be cleaned up eventually
(defn- ts-now []
  (.getTime ^js (js/Date.)))

(def ^:private fetch-fields-requested-at (atom 0))

(defn- should-fetch-fields?
  []
  (let [now (ts-now)]
    ;; Debounce with 1000 ms interval
    (> (abs (- now @fetch-fields-requested-at)) 1000)))

;;; ::fetch-fields
;;; ::fetch-fields
(defn- fetch-fields [{db :db} [_ force?]]
  (if (or force? (should-fetch-fields?))
    (do
      (reset! fetch-fields-requested-at (ts-now))
      {:http-xhrio
       {:method :get
        :uri (broutes/get-url "form_entities#available_fields"
                              {:p_id (subs/get-current-project-id db)})
        :response-format (ajax/json-response-format {:keywords? true})
        :on-success [::got-fields]
        :on-failure [::api-error ::fetch-fields {}]}})
    ;; Do nothing in case fetch fields is already requested within 1000 ms
    {}))

(re-frame/reg-event-fx
  ::fetch-fields
  fetch-fields)

;;; ::got-users
(defn got-users [db [_ users]]
  (let [groups (sort-by :name (filter #(= "group" (:type %)) users))
        users  (sort-by :name (filter #(= "user" (:type %)) users))]
    (assoc db :users users
           :groups groups)))

(re-frame/reg-event-db
  ::got-users
  got-users)

;;; ::fetch-users
(defn fetch-users [{db :db} _]
  {:http-xhrio
   {:method          :get
    :uri             (broutes/get-url "projects#users_and_groups2"
                                      {:id (subs/get-current-project-id db)})
    :response-format (ajax/json-response-format {:keywords? true})
    :on-success      [::got-users]
    :on-failure      [::api-error ::fetch-users {}]}})

(re-frame/reg-event-fx
  ::fetch-users
  fetch-users)

(defn find-key-by-db-name [value]
  (some (fn [[k v]] (when (= (:db-entity v) value) k)) entities-metadata))

(defn process-sections
  [sections]
  (->> sections
       (group-by (fn [section]
                   (let [entity-type (find-key-by-db-name (:entity_type section))]
                     entity-type)))
       (into {}
             (map (fn [[k v]]
                    [k (vec v)])))))

(defn got-sections
  [{:keys [db]} [_ is-from-settings-page? sections]]
  {:db (assoc db :entity-sections (process-sections sections))
   :fx [(when is-from-settings-page?
          [:dispatch [::fetch-fields true]])]})

(re-frame/reg-event-fx
  ::got-sections
  got-sections)

(defn fetch-sections [{db :db} [_ is-from-settings-page?]]
  {:http-xhrio
   {:method :get
    :uri (broutes/get-url "form_entities#sections"
                          {:p_id (subs/get-current-project-id db)}
                          {:is-from-settings-page (boolean is-from-settings-page?)})
    :response-format (ajax/json-response-format {:keywords? true})
    :on-success [::got-sections is-from-settings-page?]
    :on-failure [::api-error ::fetch-sections {}]}})

(re-frame/reg-event-fx
  ::fetch-sections
  fetch-sections)

;;; ::got-tags
(defn got-tags [db [_ tags]]
  (assoc db :tags (->> tags (sort-by last) (reverse))))

(re-frame/reg-event-db
  ::got-tags
  got-tags)

;;; ::fetch-tags
(defn fetch-tags [{db :db} _]
  {:http-xhrio
   {:method          :get
    :uri             (broutes/get-url "tags#cloud")
    :response-format (ajax/json-response-format {:keywords? true})
    :on-success      [::got-tags]
    :on-failure      [::api-error ::fetch-tags {}]}})

(re-frame/reg-event-fx
  ::fetch-tags
  fetch-tags)

;;; ::got-entity

(defn got-entity [{:keys [db]} [_ form-id mode {{:keys [type] :as entity} :entity
                                                :as response}]]
  (let [type (keyword type)
        page-title (str (get-entity-name type :singular-name)
                        " #" (:display_id entity) " - " (:name entity))]
    (dom/update-page-title page-title)
    {:db (-> db
             (update :progress disj ::fetch-entity)
             (assoc-in [:entity type mode] (assoc entity :type type))
             (assoc-in [:entity type :traceability] (:traceability response)))
     :fx [[:dispatch [:practitest.frontend.components2.entity-form/set-notification-state
                      form-id
                      {:status     "success"
                       :type       :loaded
                       ;; TODO: updated_by ?
                       :updated-at (:updated_at entity)}]]]}))

(re-frame/reg-event-fx
  ::got-entity
  got-entity)

;;; ::fetch-entity
(defn fetch-entity [{db :db} [_ mode]]
  (let [current-route (router/get-current-route)
        entity-type (:entity (:data current-route))
        entity-id (case entity-type
                    :milestone
                    (get-in current-route [:parameters :path :milestone-id])

                    :testset
                    (get-in current-route [:parameters :path :testset-id])

                    :issue
                    (get-in current-route [:parameters :path :issue-id]))
        ;; TODO: fix this
        form-id (case entity-type
                  :milestone
                  :milestone-main

                  :issue
                  :issue-main

                  :testset
                  :testset)
        project-id (get-in current-route [:parameters :path :project-id])]
    {:db (update db :progress conj ::fetch-entity)
     :http-xhrio
     {:method          :get
      :uri             (case entity-type
                         ;; TODO: fix this ?
                         :milestone
                         (broutes/get-url "milestones#entity"
                                          {:p_id project-id
                                           :id   entity-id})

                         :testset
                         (broutes/get-url "suites#entity"
                                          {:p_id project-id
                                           :id   entity-id})

                         :issue
                         (broutes/get-url "issues#entity"
                                          {:p_id project-id
                                           :id   entity-id}))
      :response-format (ajax/json-response-format {:keywords? true})
      :on-success      [::got-entity form-id mode]
      :on-failure      [::api-error ::fetch-entity {}]}}))

(re-frame/reg-event-fx
  ::fetch-entity
  fetch-entity)

;;; ::got-history
(defn got-history [db [_ entity-type response]]
  (-> db
      (assoc-in [:entity entity-type :history] (:data response))
      (merge-referenced-users response)
      (update :progress disj ::fetch-history)))

(re-frame/reg-event-db
  ::got-history
  got-history)

(def history-actions-map
  {:testset "suites#history"
   :qtest_run "qtest_runs#history"
   :instance "instances#history"
   :issue "issues#history"
   :requirement "requirements#history"
   :test "qtest_designs#history"
   :milestone "milestones#history"})

;;; ::fetch-history
(defn fetch-history [{db :db} _]
  (let [entity-type (subs/get-current-entity-type db)]
    {:db (update db :progress conj ::fetch-history)
     :http-xhrio
     {:method          :get
      :uri             (broutes/get-url (get history-actions-map entity-type)
                                        {:p_id (subs/get-current-project-id db)
                                         :id   (subs/get-current-entity-id db entity-type)})
      :response-format (ajax/json-response-format {:keywords? true})
      :on-success      [::got-history entity-type]
      :on-failure      [::api-error ::fetch-history {}]}}))

(re-frame/reg-event-fx
  ::fetch-history
  fetch-history)

;; Used by "legacy" History tab component (e.g. in step run history)
(re-frame/reg-event-fx
  ::fetch-history-by-uri
  (fn [{db :db} [_ entity-type history-uri]]
    {:db (update db :progress conj ::fetch-history-by-uri)
     :http-xhrio
     {:method          :get
      :uri             history-uri
      :response-format (ajax/json-response-format {:keywords? true})
      :on-success      [::got-history entity-type]
      :on-failure      [::api-error ::fetch-history-by-uri {}]}}))

(defn fetch-attachments [{db :db} _]
  (let [entity-type (subs/get-current-entity-type db)
        attachments-url (get-in entities-metadata [entity-type :urls :attachments])]
    {:db (update db :progress conj ::fetch-attachments)
     :http-xhrio
     {:method          :get
      :uri             (broutes/get-url attachments-url
                                        {:p_id (subs/get-current-project-id db)
                                         :id   (subs/get-current-entity-id db entity-type)})
      :response-format (ajax/json-response-format {:keywords? true})
      :on-success      [::got-attachments entity-type]
      :on-failure      [::api-error ::fetch-attachments {}]}}))

(re-frame/reg-event-fx
  ::fetch-attachments
  fetch-attachments)

(defn got-attachments [db [_ entity-type data]]
  (-> db
      (assoc-in [:entity entity-type :attachments] data)
      (update :progress disj ::fetch-attachments)))

(re-frame/reg-event-db
  ::got-attachments
  got-attachments)

(defn fetch-comments [{db :db} _]
  (let [entity-type (subs/get-current-entity-type db)
        comments-url (get-in entities-metadata [entity-type :urls :comments])]
    {:db (update db :progress conj ::fetch-comments)
     :http-xhrio
     {:method          :get
      :uri             (broutes/get-url comments-url
                                        {:p_id (subs/get-current-project-id db)
                                         :id   (subs/get-current-entity-id db entity-type)})
      :response-format (ajax/json-response-format {:keywords? true})
      :on-success      [::got-comments entity-type]
      :on-failure      [::api-error ::fetch-comments {}]}}))

(re-frame/reg-event-fx
  ::fetch-comments
  fetch-comments)

(defn got-comments [db [_ entity-type data]]
  (-> db
      (assoc-in [:entity entity-type :comments] data)
      (merge-referenced-users data)
      (update :progress disj ::fetch-comments)))

(re-frame/reg-event-db
  ::got-comments
  got-comments)

(defn delete-attachment [{db :db}
                         [_ {:keys [id] :as attachment}]]
  {:db (update db :progress conj [::delete-attachment id])
   :http-xhrio
   {:method          :delete
    :uri             (broutes/get-url "pattachments#destroy"
                                      {:project_id (subs/get-current-project-id db)
                                       :id         id})
    :body            (doto (js/FormData.)
                       (.append "attachment_link" "true")
                       ;; TODO: different urls ?
                       (.append "entity_type" (:entity_type attachment))
                       (.append "entity_id" (:entity_id attachment)))
    :response-format (ajax/raw-response-format)
    :on-success      [::deleted-attachment attachment]
    :on-failure      [::api-error [::delete-attachment id] {:message "Error deleting attachment"}]}})

(re-frame/reg-event-fx
  ::delete-attachment
  delete-attachment)

(defn deleted-attachment [{db :db} [_ attachment]]
  {:db         (update db :progress disj [::delete-attachment (:id attachment)])
   :dispatch-n [[::fetch-attachments]
                [::fetch-history]]})

(re-frame/reg-event-fx
  ::deleted-attachment
  deleted-attachment)

;;; ::collab-subscribe
(defn collab-subscribe [{{{:keys [client]} :collab} :db} [_ project-ids scopes]]
  (ws/send-message client {:action      :subscribe
                           :project-ids project-ids
                           :scopes      scopes})
  {})

(re-frame/reg-event-fx
  ::collab-subscribe
  collab-subscribe)

(defn request-presence [{{{:keys [client]} :collab} :db} [_ project-ids]]
  (ws/send-message client {:action      :presence
                           :project-ids project-ids})
  {})

(re-frame/reg-event-fx
  ::request-presence
  request-presence)

(defn collab-unsubscribe [{{{:keys [client]} :collab} :db} [_ project-ids scopes]]
  (ws/send-message client {:action      :unsubscribe
                           :project-ids project-ids
                           :scopes      scopes})
  {})

(re-frame/reg-event-fx
  ::collab-unsubscribe
  collab-unsubscribe)

(defn collab-subscribe-scopes [{:keys [db]} [_ scopes]]
  (let [project-id (get-in db [:options :project-id])
        current-scopes (get-in db [:collab :scopes] #{:sessions})
        updated-scopes (into current-scopes scopes)]
    {:db (assoc-in db [:collab :scopes] updated-scopes)
     :fx [[:dispatch [::collab-subscribe [project-id] scopes]]]}))

(re-frame/reg-event-fx
  ::collab-subscribe-scopes
  collab-subscribe-scopes)

(defn collab-unsubscribe-scopes [{:keys [db]} [_ scopes]]
  (let [project-id (get-in db [:options :project-id])
        current-scopes (get-in db [:collab :scopes] #{:sessions})
        scopes-set (set scopes)
        updated-scopes (clojure.set/difference current-scopes scopes-set)]
    {:db (assoc-in db [:collab :scopes] updated-scopes)
     :fx [[:dispatch [::collab-unsubscribe [project-id] scopes]]]}))

(re-frame/reg-event-fx
  ::collab-unsubscribe-scopes
  collab-unsubscribe-scopes)

;;; ::send-field-message
(defn send-field-message [{:keys [db]} [_ action {:keys [field-id entity-type entity-id]}]]
  (when (and field-id entity-type entity-id)
    (let [project-id (get-in db [:options :project-id])
          current-user (get-in db [:current-user])
          client (get-in db [:collab :client])]
      (when client
        (ws/send-message client {:action action
                                 :field-id (name field-id)
                                 :entity-type (name entity-type)
                                 :entity-id entity-id
                                 :project-id project-id
                                 :user (select-keys current-user [:id :name])}))))
  {})

(re-frame/reg-event-fx
  ::send-field-message
  send-field-message)

;;; ::ws-collab-open
(defn ws-collab-open [{:keys [db]} _]
  (let [stored-scopes (get-in db [:collab :scopes] #{:sessions})
        global-collab-events {"license-blocked" [[:practitest.frontend.license.events/update-license-blocked-status true]]
                              "idle-warning" [[:practitest.frontend.license.events/show-idle-session-warning]]
                              "version-update" [[:practitest.frontend.version-updates.events/handle-version-update]]}]
    {:db (-> db
             (assoc-in [:collab :state] :connected)
             (assoc-in [:options :collab-global-events] global-collab-events))
     :fx [[:dispatch [::collab-subscribe-scopes stored-scopes]]]}))

(re-frame/reg-event-fx
  ::ws-collab-open
  ws-collab-open)

;;; ::ws-collab-message
(defn relevant-update? [{options :options} {:keys [id ui_uuid]}]
  (and id ui_uuid
       (not= (str (:ui-uuid options)) (str ui_uuid))
       (= (str (:entity-id options)) (str id))))

(defn ws-collab-message [{:keys [db]} [_ message]]
  (let [action (:action message)
        custom-events (get-in db [:options :collab-custom-events action] [])
        global-events (get-in db [:options :collab-global-events action] [])
        all-events (concat custom-events global-events)]
    (if (relevant-update? db message)
      {:db (assoc-in db [:entity (:current-entity db) :changed-by] (:user message))
       :fx (into [[:dispatch [::fetch-entity :remote]]
                  [:dispatch [::fetch-tags]]
                  [:dispatch [:practitest.frontend.components.notifications/success2
                              (str "Entity changed by " (get-in message [:user :name]))
                              {:auto-close 3000}]]]
                 (for [event all-events]
                   [:dispatch event]))}
      {:db db
       :fx (for [event all-events]
             [:dispatch (conj event message)])})))

(re-frame/reg-event-fx
  ::ws-collab-message
  ws-collab-message)

;;; ::ws-collab-error
(defn ws-collab-error [db [_ error]]
  ;; TODO: proper error logging
  (log/debug :ws-collab-error error)
  db)

(re-frame/reg-event-db
  ::ws-collab-error
  ws-collab-error)

;;; ::ws-collab-close
(defn ws-collab-close [{:keys [db]} [_ code reason]]
  (log/debug :ws-collab-close code reason)
  ;; we want to always reconnect with something like 5sec interval
  ;; preserve the scopes when connection is lost for reconnection
  (let [current-scopes (get-in db [:collab :scopes] #{:sessions})]
    {:db (assoc db :collab {:state :disconnected :scopes current-scopes})
     :fx [[:dispatch-later {:ms 5000 :dispatch [::init-collab-ws]}]]}))

(re-frame/reg-event-fx
  ::ws-collab-close
  ws-collab-close)

;;; ::init-collab-ws
(defn init-collab-ws [{{{:keys [collab-url collab-token current-account]} :options :as db} :db} _]
  (if (str/blank? collab-url)
    (do
      (js/console.log "collab is disabled")
      {})
    (let [ws-url (str collab-url "/ws/1/updates?access-token=" collab-token
                      "&version=" FRONTEND_VERSION
                      "&account-id=" (:id current-account))
          ws     (ws/make-ws
                   {:url        ws-url
                    :format     :json
                    :on-open    #(re-frame/dispatch [::ws-collab-open])
                    :on-message #(re-frame/dispatch [::ws-collab-message %])
                    :on-error   #(re-frame/dispatch [::ws-collab-error %])
                    :on-close   #(re-frame/dispatch [::ws-collab-close %1 %2])})]
      {:db (assoc-in db [:collab :client] ws)})))

(re-frame/reg-event-fx
  ::init-collab-ws
  init-collab-ws)

(defn- process-user-projects
  [user-projects]
  (when user-projects
    (mapv
      (fn [up]
        (if (contains? up :permissions)
          (update up :permissions set)
          up))
      user-projects)))

;;; ::initialize-db
(defn initialize-db [{:keys [db]} [_ options current-entity custom-events]]
  (let [;; TODO: fix this mess, get your state from URI
        current-entity (or (get-in db [:current-route :data :entity])
                           current-entity)
        entity-id (:entity-id options)]
    {:db (merge db
                {:options         (merge (:options db)
                                         options)
                 :current-entity  current-entity
                 :current-project (:current-project options)
                 :current-user    (:current-user options)
                 :user-projects   (process-user-projects (:user-projects options))
                 :user-accounts   (:user-accounts options)
                 :current-account (:current-account options)
                 :license-blocked? (:license-blocked? options)
                 :progress        #{}})
     :fx (into
           [[:dispatch [::fetch-fields]]

           ;; TODO: load from HTML (same as tags / kanban lanes)
            [:dispatch [::fetch-users]]
            [:dispatch [::fetch-sections]]
            [:dispatch [::init-collab-ws]]

           ;; Use static tags if available (skip one network call)
            (if-let [tags (:tags options)]
              [:dispatch [::got-tags tags]]
              [:dispatch [::fetch-tags]])

           ;; Use static kanban lanes if available (skip one network call)
            (if-let [lanes (:kanban-lanes options)]
              [:dispatch [::kanban/got-lanes lanes]]
              [:dispatch [::kanban/fetch-lanes]])]

           (for [event custom-events
                 :when event]
             [:dispatch event]))}))

(re-frame/reg-event-fx
  ::initialize-db
  initialize-db)

(defn process-assigned-to
  [state]
  ;; TODO: cleanup this
  ;; Special assigned_to handling - backend expectes string "<Type>-<id>"
  (when (and (contains? state :assigned_to)
             ;; We want to send new assigned_to only if it's a map (was changed by user)
             (or (map? (:assigned_to state))
                 ;; Special case - assigned_to was cleared by the user
                 (nil? (:id (:assigned_to state)))))
    {:assigned_to_update (if-let [id (:id (:assigned_to state))]
                           (str (str/capitalize (:type (:assigned_to state)))
                                "-"
                                id)
                           ;; Alow clearing of assigned_to user
                           nil)}))

;;; ::save-entity
(defn state->params [fields state module-state]
  ;; TODO: fix this (entity-type related)
  ;; TODO: do we really need all those pseudo-generic entity handling code ?
  (let [custom-fields (filter #(:custom? (get fields %)) (keys state))
        system-fields (->> (keys state)
                           (filter #(:system? (get fields %)))
                           (remove #{:created_at :updated_at :run_status :last_run :tags :kanban_lane
                                     :assigned_to :author}))
        ;; special case for :name field
        system-fields (conj system-fields :name)

        user-fields   (filter #(= :user (:type (get fields %))) custom-fields)

        params        {:suite  (merge (select-keys state system-fields)
                                      (cond-> {}
                                        (contains? state :tags)
                                        (assoc :all_tags_list (str/join "," (:tags state))))

                                      (process-assigned-to state)

                                      ;; Special backend param to add instances
                                      (when-let [ids (:instances-to-add module-state)]
                                        {:new_form_starting_with_test_ids ids})
                                      ;; TODO: is it really used anywhere?
                                      (when-let [created_from (:created-from module-state)]
                                        {:created_from created_from}))

                       :custom (merge
                                ;; user fields will be overwritten by subsequent merge
                                 (select-keys state custom-fields)

                                ;; Users fields handling (backend expects user id as a string in custom fields)
                                 (reduce (fn [acc key]
                                           (if (contains? state key)
                                             (assoc acc key (str (or (:id (get state key))
                                                                     (get state key))))
                                             acc))
                                         {}
                                         user-fields))

                       ;; TODO: fix this with new backend implementation
                       :kanban (:lane_id (:kanban_lane state))}

        ;; cleanup
        params (cond-> params
                 (empty? (:suite params))
                 (dissoc params :suite)

                 (not (contains? state :kanban_lane))
                 (dissoc params :kanban))]
    params))

;;; ::set-field-value
(defn set-field-value [db [_ entity field value]]
  (assoc-in db [:entity entity :state field] value))

(defn reset-field-value [db [_ entity field]]
  (update-in db [:entity entity :state]
             dissoc field))

(defn set-field-value* [{:keys [db]} event]
  {:db (set-field-value db event)})

(re-frame/reg-event-fx
  ::set-field-value
  [; Disabled for now
   ;(re-frame/after ls/entity-state->local-store)
   ]
  set-field-value*)

(re-frame/reg-event-db
  ::set-form-state
  (fn [db [_ form-id new-state]]
    (assoc-in db [:form-states form-id] new-state)))

(defn set-field-state
  [db [_ form-id field-id new-state]]
  (assoc-in db [:form-states form-id :fields field-id] new-state))

(re-frame/reg-event-db
  ::set-field-state
  set-field-state)

(defn clear-linked-lists [db [_ entity field]]
  (let [fields     (:fields db)
        field-spec (get fields field)
        children   (filter #(= (:parent_list_id %) (:id field-spec)) (vals fields))]

    (reduce
      (fn [db item]
        (-> db
            (set-field-value [::set-field-value entity (:name item)])
           ;; recursively descend
            (clear-linked-lists [::clear-linked-lists entity (:name item)])))
      db
      children)))

(re-frame/reg-event-db
  ::clear-linked-lists
  clear-linked-lists)

;;; ::refresh-entity
(defn refresh-entity [db [_ entity]]
  (let [remote (get-in db [:entity entity :remote])]
    (update-in db [:entity entity]
               #(-> % (dissoc :remote) (assoc :current remote)))))

(re-frame/reg-event-db
  ::refresh-entity
  refresh-entity)

(re-frame/reg-event-fx
  ::project-pin
  (fn [{db :db} [_ user-project]]
    {:http-xhrio {:method          :post
                  :uri             (broutes/get-url "users#pin_user_project"
                                                    {:id (:id (subs/get-current-user db))})
                  :params          {:is_pinned  (not (:is_pinned user-project))
                                    :project_id (:id (:project user-project))}
                  :format          (ajax/json-request-format)
                  :response-format (ajax/json-response-format {:keywords? true})
                  :on-success      [::project-pinned user-project]
                  :on-failure      [::api-error ::project-pin {}]}}))

(re-frame/reg-event-db
  ::project-pinned
  (fn [db [_ user-project response]]
    (assoc db :user-projects (process-user-projects response))))

(re-frame/reg-event-db
  ::system-message-hidden
  (fn [db [_ id _]]
    (js/console.log ::system-message-hidden id)
    (update-in db [:options :system-messages]
               (fn [system-messages]
                 (vec
                   (remove #(= id (:id %)) system-messages))))))

(re-frame/reg-event-fx
  ::hide-system-message
  (fn [_ [_ id uri]]
    {:fx [[:http-xhrio {:method :post
                        :uri uri
                        :format (ajax/json-request-format)
                        :response-format (ajax/raw-response-format)
                        :on-success [::system-message-hidden id]
                        :on-failure [::api-error ::hide-system-message {}]}]]}))

;;; filters
;; moved over from `testset/events`

(defn got-filters [db [_ _entity path filters]]
  (-> db
      ;; This endpoint returns tree already - no need to recombine it
      (assoc-in path filters)
      (update :progress disj ::fetch-filters)))

(re-frame/reg-event-db
  ::got-filters
  got-filters)

(defn get-filter-name [{db :db} [_ entity]]
  (let [entity-name (get {:test "Test"
                          :testset "TestSet"
                          :requirement "Requirement"
                          :issue "Issue"} entity)]
    entity-name))

(defn fetch-filters [{db :db} [_ entity path]]
  (let [entity-name (get {:test "Test"
                          :testset "TestSet"
                          :requirement "Requirement"
                          :issue "Issue"} entity)]
    {:db (update db :progress conj ::fetch-filters)
     :http-xhrio
     {:method :get
      :uri (broutes/get-url "custom_views#left_pane_views"
                            {:project_id (subs/get-current-project-id db)})
      :format :url
      :vec-strategy :rails
      :params {:entity_name entity-name}
      :response-format (ajax/json-response-format {:keywords? true})
      :on-success [::got-filters entity path]
      :on-failure [::api-error ::fetch-filters {}]}}))

(re-frame/reg-event-fx
  ::fetch-filters
  fetch-filters)

(defn- fetch-settings-fields [{db :db} [_]]
  {:http-xhrio
   {:method :get
    :uri (broutes/get-url "sections#settings_fields"
                          {:project_id (subs/get-current-project-id db)})
    :response-format (ajax/json-response-format {:keywords? true})
    :on-success [::got-settings-fields]
    :on-failure [::api-error ::fetch-settings-fields {}]}})

(re-frame/reg-event-fx
  ::save-sections-success
  (fn [{db :db} [_ response]]
    {:db (-> db
             (assoc-in [:sections :is-dirty] false)
             (assoc-in [:sections :is-saving] false))
     :dispatch-n [[::fetch-fields true]
                  [::fetch-sections]
                  [::notifications/success2 (get-translation "sections.notify.saved" "Data was successfully saved")]]}))

(re-frame/reg-event-fx
  ::save-sections
  (fn save-sections [{db :db} [_ payload]]
    {:http-xhrio
     {:method :post
      :uri (broutes/get-url "sections#update_entity_fields"
                            {:project_id (subs/get-current-project-id db)})
      :params payload
      :format (ajax/json-request-format)
      :response-format (ajax/json-response-format {:keywords? true})
      :on-success [::save-sections-success]
      :on-failure [::api-error ::save-sections {:message (get-translation "sections.notify.error-saving" "Failed to save data")} :throw? true]}}))

(re-frame/reg-event-fx
  ::fetch-settings-fields
  fetch-settings-fields)

(defn- got-settings-fields [db [_ data]]
  (assoc db :settings-fields data))

(re-frame/reg-event-db
  ::got-settings-fields
  got-settings-fields)

(re-frame/reg-event-db
  ::set-active-sub-tab
  (fn [db [_ tab-id]]
    (when-let [main-state (get-in db [:entity-form :main-state])]
      (swap! main-state assoc :active-sub-tab tab-id))
    (grid-storage/save-entity-form-active-tab tab-id)
    (assoc-in db [:entity-form :active-sub-tab] tab-id)))

(re-frame/reg-sub
  ::active-sub-tab
  (fn [db [_ default-tab]]
    (or (get-in db [:entity-form :active-sub-tab])
        default-tab)))
